/**
 * Barrel re-export for all benchmark query hooks and shared helpers.
 *
 * @module hooks/useBenchmarkData
 * @see docs/api/services/benchmark.md
 */
import type {
  BenchmarkJobType,
  BenchmarkRunStatus,
  BenchmarkWorkflowRunStatus,
  BenchmarkWorkflowTrendPoint,
} from '@chatsdet/types';

// Re-export types for convenience
export type {
  BenchmarkJobType,
  BenchmarkRunStatus,
  BenchmarkWorkflowTrendPoint,
};

// Re-export everything from sub-files
export {
  type BenchmarkConfig,
  useBenchmarkConfigsQuery,
  useCreateBenchmarkConfigMutation,
  useUpdateBenchmarkConfigMutation,
  useDeleteBenchmarkConfigMutation,
  useSetDefaultConfigMutation,
} from './useBenchmarkConfigData';

export {
  type BenchmarkRun,
  useWorkflowSuitesQuery,
  useWorkflowSuiteDetailQuery,
  useTriggerWorkflowSuiteMutation,
  useWorkflowSuiteTrendsQuery,
  useWorkflowRunDetailQuery,
  useDeleteWorkflowSuiteMutation,
} from './useBenchmarkWorkflowData';

// Status styling helpers
export const runStatusVariants: Record<
  BenchmarkRunStatus,
  'secondary' | 'warning' | 'success' | 'destructive'
> = {
  pending: 'secondary',
  running: 'warning',
  completed: 'success',
  failed: 'destructive',
  timeout: 'warning',
};

export const runStatusLabels: Record<BenchmarkRunStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  timeout: 'Timed Out',
};

export const jobTypeLabels: Record<BenchmarkJobType, string> = {
  workflow_suite: 'Workflow Suite',
};

export const workflowRunStatusVariants: Record<
  BenchmarkWorkflowRunStatus,
  'secondary' | 'warning' | 'success' | 'destructive'
> = {
  pending: 'secondary',
  running: 'warning',
  completed: 'success',
  failed: 'destructive',
  skipped: 'secondary',
};

export const workflowRunStatusLabels: Record<BenchmarkWorkflowRunStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

// Format helpers
export const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

